/**
 * 来訪 `/reception/visits`(設計書 §9.1、計画 第 4 段)。
 * 見える来訪の一覧(日付・拠点・状態・自分の担当で絞る)と「来訪を登録」。
 * 登録できるのは host / operator / admin。担当者を選べるのは operator / admin(host は自分)。
 * 拠点の viewer には来訪者の氏名・件名が出ない(API が null で返す)。
 */
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Typography,
  message,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useApi, useMe } from "../../lib/useApi";
import { useDebounced } from "../../lib/useDebounced";
import { hasExactRole, hasRole } from "../../lib/roles";
import { DataTable, PageHeader, StatusTag, type DataColumn } from "../../ui";
import { errorText, useInvalidateReception, useReceptionPerms } from "./hooks";
import {
  VISIT_STATUS,
  fmtRange,
  newIdemKey,
  useCoreSend,
  useMyLocations,
  useVisits,
  type VisitListItem,
  type VisitorInput,
} from "./visits";

const { Text } = Typography;
const PAGE = 50;

export function VisitsPage() {
  const me = useMe();
  const navigate = useNavigate();
  const { readOnly } = useReceptionPerms();
  const canCreate =
    hasRole(me.data, "reception:operator") || hasExactRole(me.data, "reception:host");
  const locations = useMyLocations();

  const [range, setRange] = useState<[Dayjs, Dayjs] | null>([dayjs().startOf("day"), dayjs().add(30, "day")]);
  const [locationId, setLocationId] = useState<number | null>(null);
  const [status, setStatus] = useState<string[]>([]);
  const [mine, setMine] = useState(false);
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);

  const params = useMemo(() => {
    const p = new URLSearchParams({ limit: String(PAGE), offset: String((page - 1) * PAGE) });
    if (range) {
      p.set("date_from", range[0].format("YYYY-MM-DD"));
      p.set("date_to", range[1].format("YYYY-MM-DD"));
    }
    if (locationId !== null) p.set("location_id", String(locationId));
    for (const s of status) p.append("status", s);
    if (mine) p.set("mine", "true");
    return p;
  }, [range, locationId, status, mine, page]);
  const query = useVisits(params);

  const columns: DataColumn<VisitListItem>[] = [
    { title: "日時", ellipsis: false, width: 320, render: (_: unknown, v) => fmtRange(v.starts_at, v.ends_at) },
    { title: "拠点", ellipsis: false, width: 160, render: (_: unknown, v) => v.location?.name ?? `#${v.location_id}` },
    { title: "担当者", ellipsis: false, width: 160, render: (_: unknown, v) => v.host?.name ?? "(不明)" },
    {
      title: "来訪者",
      ellipsis: false,
      width: 320,
      render: (_: unknown, v) =>
        v.visitors.length === 0 ? (
          <Text type="secondary">なし</Text>
        ) : v.visitors[0].pii_hidden ? (
          <Text type="secondary">{v.visitor_count} 名(氏名は非表示)</Text>
        ) : (
          v.visitors.map((x) => [x.name, x.company].filter(Boolean).join(" / ")).join("、")
        ),
    },
    { title: "件名", ellipsis: true, onCell: (row) => ({ title: row.title ?? "—" }), width: 320, render: (_: unknown, v) => v.title ?? <Text type="secondary">—</Text> },
    {
      title: "受付",
      ellipsis: false,
      width: 110,
      render: (_: unknown, v) => (
        <span className="ui-num">
          {v.checked_in_count}/{v.visitor_count}
        </span>
      ),
    },
    {
      title: "状態",
      ellipsis: false,
      width: 110,
      render: (_: unknown, v) => {
        const s = VISIT_STATUS[v.status];
        return <StatusTag tone={s?.tone ?? "neutral"}>{s?.label ?? v.status}</StatusTag>;
      },
    },
  ];

  const locOptions = (locations.data?.items ?? []).map((l) => ({ value: l.id, label: l.name }));

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="来訪"
          description="自分が担当する来訪と、担当拠点の来訪。招待メールは来訪者ごとに送る(送信は送信待ちの列から順に)。"
          primary={
            canCreate ? (
              <Button type="primary" icon={<PlusOutlined />} disabled={readOnly} onClick={() => setCreating(true)}>
                来訪を登録
              </Button>
            ) : undefined
          }
        />
        {locations.data?.unassigned && (
          <Alert type="info" showIcon style={{ marginBottom: 12 }} message="担当拠点が未設定です。拠点の来訪を見るには、管理者に拠点メンバーへの追加を依頼してください。" />
        )}
        <Space wrap style={{ marginBottom: 12 }}>
          <DatePicker.RangePicker
            value={range}
            onChange={(v) => {
              setRange(v && v[0] && v[1] ? [v[0], v[1]] : null);
              setPage(1);
            }}
            allowClear
          />
          <Select
            allowClear
            style={{ width: 180 }}
            placeholder="拠点"
            options={locOptions}
            value={locationId ?? undefined}
            onChange={(v) => {
              setLocationId(v ?? null);
              setPage(1);
            }}
          />
          <Select
            mode="multiple"
            allowClear
            style={{ minWidth: 200 }}
            placeholder="状態"
            options={Object.entries(VISIT_STATUS).map(([value, s]) => ({ value, label: s.label }))}
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
          />
          <Space size={4}>
            <Switch
              size="small"
              checked={mine}
              onChange={(v) => {
                setMine(v);
                setPage(1);
              }}
            />
            <Text>自分の担当だけ</Text>
          </Space>
        </Space>
        {query.isError && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={errorText(query.error)} />}
        <DataTable
          columns={columns}
          rows={query.data?.items ?? []}
          rowKey={(v) => v.id}
          loading={query.isLoading}
          total={query.data?.total ?? 0}
          page={page}
          pageSize={PAGE}
          onPage={(p) => setPage(p)}
          onRowClick={(v) => navigate(`/reception/visits/${v.id}`)}
          empty={{ text: "この条件の来訪はありません" }}
          error={query.isError ? "来訪を読み込めませんでした" : undefined}
        />
      </div>
      {creating && (
        <CreateVisitModal
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            navigate(`/reception/visits/${id}`);
          }}
        />
      )}
    </div>
  );
}

type CreateForm = {
  location_id: number;
  host_employee_id?: number | null;
  title?: string;
  range: [Dayjs, Dayjs];
  expected_headcount?: number | null;
  note?: string;
  visitors: (VisitorInput & { email?: string })[];
};

function CreateVisitModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: number) => void }) {
  const me = useMe();
  const send = useCoreSend();
  const invalidate = useInvalidateReception();
  const locations = useMyLocations();
  const pickHost = hasRole(me.data, "reception:operator");
  const [form] = Form.useForm<CreateForm>();
  // 開いている間は同じキー(二重押し・再試行は 1 件にまとまる)
  const [idem] = useState(newIdemKey);

  const create = useMutation({
    mutationFn: (v: CreateForm) =>
      send<{ id: number }>(
        "POST",
        "/visits",
        {
          location_id: v.location_id,
          ...(pickHost && v.host_employee_id ? { host_employee_id: v.host_employee_id } : {}),
          title: v.title ?? "",
          starts_at: v.range[0].toISOString(),
          ends_at: v.range[1].toISOString(),
          expected_headcount: v.expected_headcount ?? null,
          note: v.note ?? "",
          visitors: (v.visitors ?? []).map((x) => ({
            name: x.name,
            name_kana: x.name_kana ?? "",
            company: x.company ?? "",
            email: x.email ? x.email : null,
            send_invite: x.send_invite,
            consent_declared: x.consent_declared ?? false,
          })),
        },
        idem,
      ),
    onSuccess: (r) => {
      invalidate();
      void message.success("来訪を登録しました");
      onCreated(r.id);
    },
  });

  const locOptions = (locations.data?.items ?? [])
    .filter((l) => l.enabled)
    .map((l) => ({ value: l.id, label: l.name }));

  return (
    <Modal
      open
      title="来訪を登録"
      width={760}
      onCancel={onClose}
      okText="登録"
      confirmLoading={create.isPending}
      onOk={() => form.submit()}
      destroyOnClose
    >
      {create.isError && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={errorText(create.error)} />}
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          range: [dayjs().add(1, "day").hour(10).minute(0).second(0), dayjs().add(1, "day").hour(11).minute(0).second(0)],
          visitors: [{ name: "", send_invite: true }],
        }}
        onFinish={(v) => create.mutate(v)}
      >
        <Space wrap align="start">
          <Form.Item name="location_id" label="拠点" rules={[{ required: true, message: "拠点を選ぶ" }]}>
            <Select style={{ width: 220 }} options={locOptions} placeholder="拠点" />
          </Form.Item>
          {pickHost && (
            <Form.Item name="host_employee_id" label="担当者(空なら自分)">
              <HostPicker />
            </Form.Item>
          )}
        </Space>
        <Form.Item
          name="range"
          label="日時"
          rules={[
            { required: true, message: "日時を入れる" },
            {
              validator: (_, v: [Dayjs, Dayjs] | undefined) =>
                !v || v[1].isAfter(v[0]) ? Promise.resolve() : Promise.reject(new Error("終わりは始まりより後")),
            },
          ]}
        >
          <DatePicker.RangePicker showTime={{ format: "HH:mm", minuteStep: 5 }} format="YYYY-MM-DD HH:mm" />
        </Form.Item>
        <Space wrap align="start">
          <Form.Item name="title" label="件名">
            <Input style={{ width: 360 }} maxLength={200} />
          </Form.Item>
          <Form.Item name="expected_headcount" label="予定人数">
            <InputNumber min={1} max={1000} />
          </Form.Item>
        </Space>
        <Form.Item name="note" label="メモ(受付向け)">
          <Input.TextArea rows={2} maxLength={2000} />
        </Form.Item>
        <Text strong>来訪者</Text>
        <Form.List name="visitors">
          {(fields, { add, remove }) => (
            <>
              {fields.map((f) => (
                <Space key={f.key} wrap align="start" style={{ display: "flex", marginTop: 8 }}>
                  <Form.Item name={[f.name, "name"]} rules={[{ required: true, message: "氏名" }]} style={{ marginBottom: 4 }}>
                    <Input placeholder="氏名" style={{ width: 140 }} maxLength={200} />
                  </Form.Item>
                  <Form.Item name={[f.name, "name_kana"]} style={{ marginBottom: 4 }}>
                    <Input placeholder="カナ" style={{ width: 120 }} maxLength={200} />
                  </Form.Item>
                  <Form.Item name={[f.name, "company"]} style={{ marginBottom: 4 }}>
                    <Input placeholder="会社" style={{ width: 140 }} maxLength={200} />
                  </Form.Item>
                  <Form.Item name={[f.name, "email"]} rules={[{ type: "email", message: "メールの形" }]} style={{ marginBottom: 4 }}>
                    <Input placeholder="メール(招待の宛先)" style={{ width: 200 }} />
                  </Form.Item>
                  <Form.Item name={[f.name, "send_invite"]} valuePropName="checked" style={{ marginBottom: 4 }}>
                    <Checkbox>招待を送る</Checkbox>
                  </Form.Item>
                  <Form.Item name={[f.name, "consent_declared"]} valuePropName="checked" style={{ marginBottom: 4 }}>
                    <Checkbox>同意を口頭で得た</Checkbox>
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(f.name)} style={{ marginTop: 8 }} aria-label="この来訪者を外す" />
                </Space>
              ))}
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                style={{ marginTop: 8 }}
                disabled={fields.length >= 50}
                onClick={() => add({ name: "", send_invite: true })}
              >
                来訪者を追加
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
}

/** 担当者(全社の在籍者から選ぶ。operator / admin だけ)。 */
export function HostPicker({ value, onChange }: { value?: number | null; onChange?: (id: number | null) => void }) {
  const api = useApi();
  const [input, setInput] = useState("");
  const q = useDebounced(input);
  const query = useQuery({
    queryKey: ["directory", "employees", "reception-host", q],
    queryFn: () =>
      api.directoryEmployees(new URLSearchParams({ limit: "20", offset: "0", status: "active", ...(q ? { q } : {}) })),
  });
  return (
    <Select
      showSearch
      allowClear
      style={{ width: 260 }}
      placeholder="氏名 / 社員番号"
      filterOption={false}
      loading={query.isFetching}
      searchValue={input}
      onSearch={setInput}
      value={value ?? undefined}
      options={(query.data?.items ?? []).map((e) => ({ value: e.id, label: `${e.name}(${e.emp_code})` }))}
      onChange={(v) => onChange?.(v ?? null)}
      notFoundContent={query.isFetching ? "検索中…" : "該当する従業員なし"}
    />
  );
}
